import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MessageBar, MessageBarBody, Spinner, Text, makeStyles } from '@fluentui/react-components';
import { isEmployed } from '@vigeo/shared';
import { tokens } from '@vigeo/ui';
import { EmployeeProfile } from '@/features/employee-profile';
import { fetchPerson } from '@/lib/api';
import { fetchAuthState } from '@/lib/auth';
import { ROSTER_PATH, sectionOn } from '@/lib/sections';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
});

/**
 * One employee's profile, reached from the Roster (#585). The same person record the candidate page reads
 * (`/app/people/:id`), under the same query key; that page stays as it is for hiring.
 */
export function EmployeeProfilePage() {
  const styles = useStyles();
  const { id = '' } = useParams();
  const auth = useQuery({ queryKey: ['auth'], queryFn: fetchAuthState, retry: false });
  const person = useQuery({ queryKey: ['person', id], queryFn: () => fetchPerson(id), enabled: id !== '' });
  const canEdit = auth.data?.status === 'signed-in' && auth.data.user.isAdmin;

  if (person.isPending) return <div className={styles.page}><Spinner size="small" label="Loading this person’s profile" /></div>;
  if (person.isError) return <MessageBar intent="error"><MessageBarBody>No such person, or you do not have access to them.</MessageBarBody></MessageBar>;
  /*
   * THE ROSTER'S PROFILE IS FOR STAFF (#722). Without Hiring in this build, a person at any other stage
   * is not on the Roster: the profile would otherwise draw them with the hiring checklist and a
   * "Came through hiring" line, from an address typed or pasted. The server turns them away too
   * (`answersForPerson`); this is the page's own answer for a server that has not caught up.
   */
  if (!sectionOn('hiring') && !isEmployed(person.data.lifecycle)) {
    return (
      <div className={styles.page}>
        <MessageBar intent="info"><MessageBarBody>This person is not on the Roster.</MessageBarBody></MessageBar>
        <Link to={ROSTER_PATH}><Text>Back to the Roster</Text></Link>
      </div>
    );
  }
  return <EmployeeProfile person={person.data} answers={person.data.profileAnswers} canEdit={canEdit} />;
}
